// Pure routing guard: once a pv_reception task has been submitted, no edit
// step should remain reachable — any attempt redirects to the read-only
// SubmittedView (mounted at the bare /pv/$taskId route, see
// routes/pv.$taskId.tsx).
//
// Mirrors the server's actual invariant (assertRenderPayloadEditable in
// apps/api/src/modules/renders/render-editable.ts): for pv_reception tasks,
// the render payload is locked once `submitted_at` is stamped, and that
// stamp is never cleared — even when an admin later moves the task
// submitted -> completed/questioned/in_progress during review. Locking on
// `status === "submitted"` instead would make the client treat a
// post-review task as editable while the server keeps 409'ing every write
// with render_locked.
import type { Task } from "@relais/contracts";

type LockableTask = Pick<Task, "submitted_at">;

/** True once the task is locked for editing from the poseur's point of view. */
export function isPvTaskLocked(task: LockableTask): boolean {
  return task.submitted_at != null;
}

/**
 * True when the current location is a deeper edit-step URL
 * (`/pv/$taskId/$step`) on a locked task — the layout must redirect it back
 * to `barePath`, which then renders SubmittedView.
 */
export function shouldRedirectToSubmittedView(params: {
  task: LockableTask;
  pathname: string;
  barePath: string;
}): boolean {
  return isPvTaskLocked(params.task) && params.pathname !== params.barePath;
}

/**
 * True when the bare `/pv/$taskId` route should redirect forward to the
 * first wizard step instead of rendering SubmittedView — i.e. the task is
 * still editable.
 */
export function shouldRedirectToFirstStep(task: LockableTask): boolean {
  return !isPvTaskLocked(task);
}
